
import React, { useState, useEffect, useContext } from 'react';
import { Puzzle, Page, defaultImageEdits, ImageEdits, DEFAULT_THEME_COLOR, PageContextType, getSafeImageEdits } from '../types';
import { useAuth } from '../AuthContext';
import { usePuzzles } from '../PuzzleContext';
// FIX: Changed import to correct PageContext file to avoid circular dependency
import { PageContext } from '../PageContext';
import CameraModal from '../components/CameraModal';
import { uploadFile, base64ToBlob } from '../storageUtils';
import AIImageGeneratorModal from '../components/AIImageGeneratorModal';
import AIClueGeneratorModal from '../components/AIClueGeneratorModal';
import AudioRecorder from '../components/AudioRecorder';
import BackButton from '../components/BackButton';

interface CreatePostPageProps {
    puzzleToEdit?: Puzzle | null;
    groupId?: string;
    openAI?: boolean;
    draftData?: any;
}

const standardCategories = [
    "What was it?", 
    "Where am I?", 
    "Who is it?", 
    "What film is it?",
    "What's that song?",
    "What's the lyrics?",
    "Who's the artist?",
    "What's the year?",
    "What's that noise?",
    "Sneakers & Trainers",
    "Apparel & Clothes",
    "Toys & Games",
    "Tech & Electronics",
    "Featured Brands"
];

const CreatePostPage: React.FC<CreatePostPageProps> = (props) => {
    const { puzzleToEdit, groupId, openAI = false } = props;
    const { userProfile } = useAuth();
    const { savePuzzle } = usePuzzles();
    const pageContext = useContext<PageContextType | undefined>(PageContext);

    const [imageUrl, setImageUrl] = useState('');
    const [clue, setClue] = useState('');
    const [answer, setAnswer] = useState('');
    const [category, setCategory] = useState('');
    const [imageEdits, setImageEdits] = useState<ImageEdits>(defaultImageEdits);
    const [showInMainFeed, setShowInMainFeed] = useState(true);
    const [isTimed, setIsTimed] = useState(false);
    const [timerDuration, setTimerDuration] = useState(60);
    const [isCameraOpen, setCameraOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [postColor, setPostColor] = useState(DEFAULT_THEME_COLOR);
    const [isAIModalOpen, setAIModalOpen] = useState(false);
    const [isAIClueModalOpen, setAIClueModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [noClue, setNoClue] = useState(false);
    const [isSponsored, setIsSponsored] = useState(false);
    const [promoLink, setPromoLink] = useState('');
    const [promoCoupon, setPromoCoupon] = useState('');

    const [audioUrl, setAudioUrl] = useState('');
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [audioStartTime, setAudioStartTime] = useState(0);
    const [audioEndTime, setAudioEndTime] = useState(30);
    const [answerAudioStartTime, setAnswerAudioStartTime] = useState(0);
    const [answerAudioEndTime, setAnswerAudioEndTime] = useState(30);
    const [postType, setPostType] = useState<'image' | 'audio'>('image');

    const availableCategories = standardCategories;

    useEffect(() => {
        if (openAI) {
            setAIModalOpen(true);
        }
    }, [openAI]);

    useEffect(() => {
        const defaultColor = userProfile?.primaryColor || DEFAULT_THEME_COLOR;
        if (props.draftData) {
            const d = props.draftData;
            setImageUrl(d.imageUrl || '');
            setClue(d.clue || '');
            setAnswer(d.answer || '');
            setCategory(d.category || availableCategories[0]);
            setImageEdits(getSafeImageEdits(d.imageEdits));
            setShowInMainFeed(d.showInMainFeed ?? true);
            setIsTimed(d.isTimed || false);
            setTimerDuration(d.timerDuration || 60);
            setPostColor(d.postColor || defaultColor);
            setNoClue(d.noClue || false);
            setIsSponsored(d.isSponsored || false);
            setPromoLink(d.promoLink || '');
            setPromoCoupon(d.promoCoupon || '');
            if (d.audioUrl) {
                setPostType('audio');
                setAudioUrl(d.audioUrl);
                if (d.audioBlob) setAudioBlob(d.audioBlob);
                setAudioStartTime(d.audioStartTime || 0);
                setAudioEndTime(d.audioEndTime || 30);
                setAnswerAudioStartTime(d.answerAudioStartTime || 0);
                setAnswerAudioEndTime(d.answerAudioEndTime || 30);
            }
        } else if (puzzleToEdit) {
            setImageUrl(puzzleToEdit.imageUrl);
            setClue(puzzleToEdit.clue);
            setAnswer(puzzleToEdit.answer);
            setCategory(puzzleToEdit.category);
            setImageEdits(getSafeImageEdits(puzzleToEdit.imageEdits));
            setShowInMainFeed(puzzleToEdit.showInMainFeed ?? true);
            setIsTimed(puzzleToEdit.type === 'timed');
            setTimerDuration(puzzleToEdit.timerDuration || 60);
            setPostColor(puzzleToEdit.postColor || defaultColor);
            setNoClue(puzzleToEdit.areCluesAllowed === false);
            setIsSponsored(puzzleToEdit.isSponsored || false);
            setPromoLink(puzzleToEdit.promoLink || '');
            setPromoCoupon(puzzleToEdit.promoCoupon || '');
            if (puzzleToEdit.audioUrl) {
                setPostType('audio');
                setAudioUrl(puzzleToEdit.audioUrl);
                setAudioStartTime(puzzleToEdit.audioStartTime || 0);
                setAudioEndTime(puzzleToEdit.audioEndTime || 30);
                setAnswerAudioStartTime(puzzleToEdit.answerAudioStartTime || 0);
                setAnswerAudioEndTime(puzzleToEdit.answerAudioEndTime || 30);
            }
        } else {
            setImageUrl('');
            setClue('');
            setAnswer('');
            setCategory(availableCategories[0]);
            setImageEdits(defaultImageEdits);
            setShowInMainFeed(true);
            setIsTimed(false);
            setTimerDuration(60);
            setPostColor(defaultColor);
            setNoClue(false);
            setAudioUrl('');
        }
    }, [puzzleToEdit, availableCategories, userProfile, props.draftData]);

    const handleSave = async () => {
        if (!answer.trim() || !category) {
            alert('Please fill in all required fields.');
            return;
        }

        setIsSaving(true);

        try {
            let finalAudioUrl = audioUrl;
            let currentBlob = audioBlob;

            // Failsafe: if somehow the state lost the Blob object but holds the blob: URL
            if (!currentBlob && postType === 'audio' && finalAudioUrl.startsWith('blob:')) {
                try {
                    const response = await fetch(finalAudioUrl);
                    currentBlob = await response.blob();
                } catch (err) {
                    console.error("Could not fetch blob from url:", finalAudioUrl, err);
                }
            }

            if (currentBlob && postType === 'audio') {
                finalAudioUrl = await uploadFile(currentBlob, `puzzle-audio/${userProfile?.uid || 'unknown'}`);
            }

            const puzzleData: any = {
                imageUrl: imageUrl || '', // Even if audio, we allow empty or default image url handling
                clue: clue.trim(),
                answer,
                category,
                likes: puzzleToEdit?.likes || [],
                dislikes: puzzleToEdit?.dislikes || [],
                hearts: puzzleToEdit?.hearts || [],
                comments: puzzleToEdit?.comments || [],
                imageEdits,
                showInMainFeed,
                showClue: !noClue,
                areCluesAllowed: !noClue,
                type: isTimed ? 'timed' : 'standard',
                postColor,
                correctGuessers: puzzleToEdit?.correctGuessers || [],
                incorrectGuessers: puzzleToEdit?.incorrectGuessers || [],
                incorrectGuesses: puzzleToEdit?.incorrectGuesses || [],
                isSponsored: (userProfile?.isAdmin || userProfile?.isBusiness) ? isSponsored : false,
                promoLink: (userProfile?.isAdmin || userProfile?.isBusiness) ? promoLink : '',
                promoCoupon: (userProfile?.isAdmin || userProfile?.isBusiness) ? promoCoupon : '',
            };
            if (groupId) puzzleData.groupId = groupId;
            if (isTimed && timerDuration) puzzleData.timerDuration = timerDuration;
            
            if (postType === 'audio' && finalAudioUrl) {
                if (finalAudioUrl.startsWith('blob:')) {
                    console.error("Audio save failed. finalAudioUrl:", finalAudioUrl, "audioUrl:", audioUrl, "currentBlob exists:", !!currentBlob);
                    alert('Error saving audio. Please remove and re-record your audio.');
                    setIsSaving(false);
                    return;
                }
                puzzleData.audioUrl = finalAudioUrl;
                puzzleData.audioStartTime = audioStartTime;
                puzzleData.audioEndTime = audioEndTime;
                puzzleData.answerAudioStartTime = answerAudioStartTime;
                puzzleData.answerAudioEndTime = answerAudioEndTime;
            }

            await savePuzzle(puzzleData, puzzleToEdit?.id);
            pageContext?.goBack();
        } catch (error: any) {
            console.error("Failed to save puzzle:", error);
            const errorMessage = error?.message || "Unknown error";
            alert(`Failed to save puzzle. ${errorMessage.includes('permission') ? 'Permission denied. Please check your account settings.' : 'Please try again.'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleGenerateClue = () => {
        if (!answer.trim()) {
            alert('Please provide an answer first to generate a clue.');
            return;
        }
        if (!imageUrl) {
            alert('Please provide an image first for the AI to analyze.');
            return;
        }
        if (!userProfile) return;

        if (userProfile.aiCredits <= 0) {
            alert("You're out of AI Credits! Please visit the AI Creator Studio to get more.");
            pageContext?.switchPage(Page.AIHome, "AI Creator Studio");
            return;
        }

        setAIClueModalOpen(true);
    };

    const handleLaunchEditor = (urlToEdit?: string) => {
        // Save current draft state to props before navigating
        pageContext?.updatePageStackProps(-1, {
            draftData: { imageUrl: urlToEdit || imageUrl, clue, answer, category, imageEdits, postColor, noClue, isTimed, showInMainFeed, timerDuration, isSponsored, promoLink, promoCoupon, audioUrl, audioBlob, audioStartTime, audioEndTime, answerAudioStartTime, answerAudioEndTime }
        });

        const contextForEditor = {
            clue,
            answer,
            category,
            postColor,
            imageUrl: urlToEdit || imageUrl,
        };

        pageContext?.launchImageEditor(
            'post',
            urlToEdit || imageUrl,
            imageEdits,
            'square',
            (newEdits: ImageEdits, newImageUrl?: string, newColor?: string) => {
                // Update local state
                const finalUrl = newImageUrl || urlToEdit || imageUrl;
                setImageEdits(newEdits);
                setImageUrl(finalUrl);
                if (newColor) setPostColor(newColor);
                
                // Also update the draft properties directly in the stack so the next mount sees them
                pageContext?.updatePageStackProps(-2, {
                    draftData: { 
                        imageUrl: finalUrl, 
                        clue, 
                        answer, 
                        category, 
                        imageEdits: newEdits, 
                        postColor: newColor || postColor, 
                        noClue, 
                        isTimed, 
                        showInMainFeed, 
                        timerDuration, 
                        isSponsored, 
                        promoLink, 
                        promoCoupon,
                        audioUrl,
                        audioBlob,
                        audioStartTime,
                        audioEndTime,
                        answerAudioStartTime,
                        answerAudioEndTime
                    }
                });
            },
            null,
            contextForEditor
        );
    };

    const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0] && userProfile) {
            const file = e.target.files[0];
            setIsUploading(true);
            try {
                const downloadURL = await uploadFile(file, `puzzle-images/${userProfile.uid}`);
                setImageUrl(downloadURL);
                handleLaunchEditor(downloadURL);
            } catch (error) {
                console.error('Image upload failed.', error);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleImageFromCamera = async (base64Image: string, mimeType: string) => {
        if (!userProfile) return;
        setCameraOpen(false);
        setIsUploading(true);
        try {
            const blob = base64ToBlob(base64Image, mimeType);
            const downloadURL = await uploadFile(blob, `puzzle-images/${userProfile.uid}`);
            setImageUrl(downloadURL);
            handleLaunchEditor(downloadURL);
        } catch (error) {
            console.error('Image upload failed.', error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleAIImageGenerated = async (base64: string) => {
        if (!userProfile) return;
        setAIModalOpen(false);
        setIsUploading(true);
        try {
            const blob = base64ToBlob(base64, 'image/jpeg');
            const downloadURL = await uploadFile(blob, `puzzle-images/${userProfile.uid}/ai`);
            setImageUrl(downloadURL);
            setImageEdits(defaultImageEdits);
            handleLaunchEditor(downloadURL);
        } catch (error) {
            console.error('AI image upload failed.', error);
        } finally {
            setIsUploading(false);
        }
    };

    const imageStyle: React.CSSProperties = {
        filter: `brightness(${imageEdits.adjustments.brightness}%) contrast(${imageEdits.adjustments.contrast}%) saturate(${imageEdits.adjustments.saturate}%) blur(${imageEdits.adjustments.blur}px)`,
        transform: `scale(${imageEdits.adjustments.zoom}) translate(${imageEdits.adjustments.offsetX}px, ${imageEdits.adjustments.offsetY}px)`,
    };

    return (
        <>
            <div className="h-full flex flex-col bg-[var(--primary-dark-purple)] text-white">
                <header className="flex-shrink-0 flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))] border-b border-white/20">
                    <BackButton colorClass="text-white" text="" />
                    <h2 className="text-xl font-bold aurora-text">{puzzleToEdit ? 'Edit Post' : 'Create Post'}</h2>
                    <div className="w-10"></div> {/* Spacer */}
                </header>

                <div className="flex-grow p-4 space-y-4 overflow-y-auto custom-scrollbar min-h-0">
                    <div className="flex bg-black/20 rounded-lg p-1">
                        <button 
                            className={`flex-1 py-2 font-bold rounded-md transition ${postType === 'image' ? 'bg-[var(--accent-pink)] text-white' : 'text-gray-400'}`}
                            onClick={() => setPostType('image')}
                        >
                            <i className="ph-fill ph-image mr-2"></i> Image Puzzle
                        </button>
                        <button 
                            className={`flex-1 py-2 font-bold rounded-md transition ${postType === 'audio' ? 'bg-[var(--accent-pink)] text-white' : 'text-gray-400'}`}
                            onClick={() => setPostType('audio')}
                        >
                            <i className="ph-fill ph-music-note mr-2"></i> Audio Puzzle
                        </button>
                    </div>

                    {postType === 'audio' && (
                        <div>
                            <AudioRecorder 
                                onAudioReady={(blob, previewUrl, duration, start, end, ansStart, ansEnd) => {
                                    if (blob) setAudioBlob(blob);
                                    else if (previewUrl === '') setAudioBlob(null);
                                    setAudioUrl(previewUrl);
                                    setAudioStartTime(start);
                                    setAudioEndTime(end);
                                    setAnswerAudioStartTime(ansStart);
                                    setAnswerAudioEndTime(ansEnd);
                                }}
                                initialAudioUrl={audioUrl}
                                initialStartTime={audioStartTime}
                                initialEndTime={audioEndTime}
                                initialAnswerStartTime={answerAudioStartTime}
                                initialAnswerEndTime={answerAudioEndTime}
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-300">
                            {postType === 'audio' ? 'Cover Art (Optional)' : 'Image'}
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-white/30 border-dashed rounded-md">
                            {isUploading ? (
                                <div className="flex flex-col items-center justify-center py-4">
                                    <div className="spinner-white"></div>
                                    <p className="mt-2 text-sm text-gray-300">Uploading image...</p>
                                </div>
                            ) : imageUrl ? (
                                <div className="w-full max-w-xs text-center">
                                    <div className="relative w-full h-48 overflow-hidden rounded-lg bg-black/20">
                                        <div className={`frame-container ${imageEdits.frame} w-full h-full`}>
                                            <div className="filter-wrapper w-full h-full">
                                                <div className={`w-full h-full ${imageEdits.filter}`}>
                                                    <img
                                                        src={imageUrl}
                                                        alt="Preview"
                                                        className="w-full h-full object-cover"
                                                        style={imageStyle}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-2 flex justify-center gap-2">
                                        <button onClick={() => setImageUrl('')} className="bg-transparent border border-white/50 text-white font-bold py-1 px-3 rounded-lg hover:bg-white/10 transition-colors">Change</button>
                                        <button onClick={() => handleLaunchEditor()} className="bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-bold py-1 px-3 rounded-lg">Edit</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-1 text-center">
                                    <svg className="mx-auto h-12 w-12 text-gray-500" stroke="currentColor" fill="none" viewBox="0 0 48 48"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    <div className="flex flex-col sm:flex-row justify-center items-center gap-2 text-sm text-gray-400">
                                        <label htmlFor="file-upload" className="relative cursor-pointer bg-transparent rounded-md font-medium text-[var(--accent-pink)] hover:text-pink-400 focus-within:outline-none">
                                            <span>Upload a file</span>
                                            <input id="file-upload" type="file" className="sr-only" onChange={handleImageSelect} accept="image/*" />
                                        </label>
                                        <p className="px-1 text-gray-400">or</p>
                                        <button type="button" onClick={() => setCameraOpen(true)} className="relative cursor-pointer bg-transparent rounded-md font-medium text-[var(--accent-pink)] hover:text-pink-400">
                                            Take a photo
                                        </button>
                                        <p className="px-1 text-gray-400">or</p>
                                        <button 
                                            type="button" 
                                            onClick={() => setAIModalOpen(true)} 
                                            className="relative cursor-pointer bg-transparent rounded-md font-bold text-[var(--accent-pink)] hover:text-pink-400"
                                        >
                                            ✨ Generate with AI
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="answer" className="block text-sm font-medium text-gray-300">Answer</label>
                        <input type="text" id="answer" value={answer} onChange={e => setAnswer(e.target.value.toUpperCase())} className="mt-1 block w-full border-white/20 rounded-md shadow-sm bg-white/10 text-white focus:ring-[var(--accent-pink)] focus:border-[var(--accent-pink)]" required spellCheck={true} autoCorrect="on" />
                    </div>
                    
                    <div>
                        <label htmlFor="clue" className="block text-sm font-medium text-gray-300">Clue (Optional)</label>
                        <input type="text" id="clue" value={clue} onChange={e => setClue(e.target.value)} className="mt-1 block w-full border-white/20 rounded-md shadow-sm bg-white/10 text-white focus:ring-[var(--accent-pink)] focus:border-[var(--accent-pink)] disabled:bg-white/5 disabled:placeholder-gray-500" placeholder="Type a clue if you want..." />
                        <button
                            type="button"
                            onClick={handleGenerateClue}
                            className="mt-2 w-full flex items-center justify-center gap-2 text-md font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg shadow-md hover:opacity-90 transition-opacity"
                        >
                            <i className="ph-duotone ph-brain"></i>
                            Generate a Cryptic Clue with AI
                        </button>
                    </div>

                    <div className="flex items-center">
                        <input type="checkbox" id="noClue" checked={noClue} onChange={(e) => setNoClue(e.target.checked)} className="h-4 w-4 text-[var(--accent-pink)] bg-transparent border-white/50 rounded focus:ring-[var(--accent-pink)]" />
                        <label htmlFor="noClue" className="ml-2 block text-sm text-gray-200">Post without using clues</label>
                    </div>

                    <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-300">Category</label>
                        <select id="category" value={category} onChange={e => setCategory(e.target.value)} className="mt-1 block w-full border-white/20 rounded-md shadow-sm bg-white/10 text-white focus:ring-[var(--accent-pink)] focus:border-[var(--accent-pink)]" required>
                            {availableCategories.map(cat => <option key={cat} value={cat} className="bg-[var(--primary-dark-purple)]">{cat}</option>)}
                        </select>
                    </div>
                    {!groupId && (
                        <div className="flex items-center">
                            <input type="checkbox" id="showInMainFeed" checked={showInMainFeed} onChange={e => setShowInMainFeed(e.target.checked)} className="h-4 w-4 text-[var(--accent-pink)] bg-transparent border-white/50 rounded focus:ring-[var(--accent-pink)]" />
                            <label htmlFor="showInMainFeed" className="ml-2 block text-sm text-gray-200">Show on my main feed (as well as profile)</label>
                        </div>
                    )}
                    {groupId && (
                        <div className="flex items-center">
                            <input type="checkbox" id="isTimed" checked={isTimed} onChange={e => setIsTimed(e.target.checked)} className="h-4 w-4 text-[var(--accent-pink)] bg-transparent border-white/50 rounded focus:ring-[var(--accent-pink)]" />
                            <label htmlFor="isTimed" className="ml-2 block text-sm text-gray-200">Make this a timed challenge for the group</label>
                        </div>
                    )}
                    {(userProfile?.isAdmin || userProfile?.isBusiness) && (
                        <div className="p-4 bg-black/20 rounded-lg border border-purple-500/30 space-y-4">
                            <h3 className="text-sm font-bold text-purple-300 uppercase tracking-wider">{userProfile?.isAdmin ? 'Admin' : 'Business'}: Sponsored Settings</h3>
                            <div className="flex items-center">
                                <input type="checkbox" id="isSponsored" checked={isSponsored} onChange={e => setIsSponsored(e.target.checked)} className="h-4 w-4 text-purple-500 bg-transparent border-white/50 rounded focus:ring-purple-500" />
                                <label htmlFor="isSponsored" className="ml-2 block text-sm text-white font-bold">Mark as Sponsored Puzzle</label>
                            </div>
                            {isSponsored && (
                                <div className="space-y-3 animate-fade-in">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400">Promo Link (URL)</label>
                                        <input type="url" value={promoLink} onChange={e => setPromoLink(e.target.value)} placeholder="https://brand.com/offer" className="mt-1 block w-full border-white/20 rounded-md shadow-sm bg-white/10 text-white text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400">Promo Coupon (Text)</label>
                                        <input type="text" value={promoCoupon} onChange={e => setPromoCoupon(e.target.value)} placeholder="SAVE20" className="mt-1 block w-full border-white/20 rounded-md shadow-sm bg-white/10 text-white text-sm" />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {isTimed && groupId && (
                        <div>
                            <label htmlFor="timerDuration" className="block text-sm font-medium text-gray-300">Timer Duration (seconds)</label>
                            <input type="number" id="timerDuration" value={timerDuration} onChange={e => setTimerDuration(parseInt(e.target.value, 10))} className="mt-1 block w-full border-white/20 rounded-md shadow-sm bg-white/10 text-white focus:ring-[var(--accent-pink)] focus:border-[var(--accent-pink)]" min="10" />
                        </div>
                    )}

                    <div className="pt-6">
                        <button
                            onClick={handleSave}
                            disabled={isUploading || isSaving || !answer.trim() || !category || (postType === 'image' && !imageUrl) || (postType === 'audio' && !audioUrl)}
                            className="w-full font-bold text-lg bg-gradient-to-r from-pink-500 to-yellow-500 text-white py-4 px-4 rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center transition-transform hover:scale-105"
                        >
                            {isSaving ? <div className="spinner-white"></div> : (puzzleToEdit ? 'Save Changes' : 'Post Puzzle')}
                        </button>
                    </div>
                </div>
            </div>
            <CameraModal
                isOpen={isCameraOpen}
                onClose={() => setCameraOpen(false)}
                onImageSelected={handleImageFromCamera}
            />
            <AIImageGeneratorModal
                isOpen={isAIModalOpen}
                onClose={() => setAIModalOpen(false)}
                onImageGenerated={handleAIImageGenerated}
            />
            {isAIClueModalOpen && (
                 <AIClueGeneratorModal
                    isOpen={isAIClueModalOpen}
                    onClose={() => setAIClueModalOpen(false)}
                    onClueAccepted={(newClue) => setClue(newClue)}
                    imageUrl={imageUrl}
                    answer={answer}
                />
            )}
        </>
    );
};

export default CreatePostPage;
